import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { canonicalCompositeSlug, compositeCueItemIdsEquivalent } from '@/helpers/composite-slug.helper';
import type { FlowControlSegmentAccent } from '@/helpers/flow-control-segment-accent.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { isNextCuePreviewTarget, resolveLiveNextCueTarget } from '@/helpers/resolve-next-cue-preview-row.helper';
import type { CueItemPhase, FlowCueStateV1 } from '@/types/player/perform-control-v1.types';

export { compositeCueItemIdsEquivalent, canonicalCompositeSlug };

export type CueItemStateEntry = FlowCueStateV1['items'][string] & {
  itemId?: string;
  cueItemId?: string;
  scope?: string;
  slotId?: string;
};

export type CueItemResolveContext = {
  scope?: string;
  slotId?: string;
  cueItemId?: string;
};

function slotIdFromCueSegmentAnchor(anchor: string | undefined): string | undefined {
  if (!anchor?.startsWith('slot:')) {
    return undefined;
  }
  return anchor.slice('slot:'.length);
}

function normalizeCueSlotId(slotId: string | undefined): string | undefined {
  if (!slotId) {
    return undefined;
  }
  return getObjectId(slotId) ?? slotId;
}

function compositeSlugFromItemId(itemId: string): string | null {
  return itemId.startsWith('composite:') ? itemId.slice('composite:'.length) : null;
}

function expandCompositeCueItemIds(itemId: string): string[] {
  const slug = compositeSlugFromItemId(itemId);
  if (!slug) {
    return [itemId];
  }
  const canonical = canonicalCompositeSlug(slug);
  const variants = new Set<string>([`composite:${slug}`, `composite:${canonical}`]);
  if (canonical === 'flow-info') {
    variants.add('composite:flow-title');
  }
  return [...variants];
}

function cueSlotIdsMatch(entrySlotId: string | undefined, contextSlotId: string | undefined): boolean {
  if (!contextSlotId) {
    return true;
  }
  const normalizedEntrySlotId = normalizeCueSlotId(entrySlotId);
  const normalizedContextSlotId = normalizeCueSlotId(contextSlotId);
  if (!normalizedEntrySlotId) {
    return true;
  }
  return normalizedEntrySlotId === normalizedContextSlotId;
}

/** True when the live cue pointer step matches this playlist item (scope, slot, cueItemId when both sides have it). */
export function matchesLiveCueCurrentStep(
  cueState: FlowCueStateV1,
  itemId: string,
  context?: CueItemResolveContext,
): boolean {
  const step = cueState.currentStep;
  if (!step) {
    return false;
  }
  if (step.itemId !== itemId && !compositeCueItemIdsEquivalent(step.itemId, itemId)) {
    return false;
  }
  if (context?.scope && step.scope !== context.scope) {
    return false;
  }
  if (context?.cueItemId && step.cueItemId && context.cueItemId !== step.cueItemId) {
    return false;
  }
  const stepSlotId = normalizeCueSlotId(slotIdFromCueSegmentAnchor(step.segmentAnchor) ?? cueState.activeSlotId);
  const contextSlotId = normalizeCueSlotId(context?.slotId);
  if (contextSlotId && stepSlotId && contextSlotId !== stepSlotId) {
    return false;
  }
  return true;
}

/**
 * While an aside is up, the list may still carry a stale playing flag on Session Info
 * or on the row Next returns to. One media row is playing. The next target is next.
 * Every other row is neither.
 */
export function resolveAsideListChrome(
  cueState: FlowCueStateV1 | undefined,
  itemId: string,
  context: CueItemResolveContext | undefined,
  insertResume?: { itemId?: string; scope?: string; slotId?: string } | null,
): 'playing' | 'next' | 'other' | null {
  if (!cueState) {
    return null;
  }
  const next = resolveLiveNextCueTarget(cueState, insertResume);
  const isNextId = (candidate: string | undefined, candidateContext?: CueItemResolveContext) =>
    isNextCuePreviewTarget(next, candidate ?? '', candidateContext);
  const onAirEntries = Object.values(cueState.items ?? {}).filter((entry) => entry.phase === 'on_air');
  const onAirMedia = onAirEntries.filter((entry) => entry.itemId?.startsWith('media:'));
  const infoAlsoPlaying = onAirEntries.some((entry) => entry.itemId != null && !entry.itemId.startsWith('media:'));
  const asideOverlay = cueState.cueOverrideActive === true || Boolean(insertResume?.itemId);
  const conflictingPlaying = asideOverlay || onAirMedia.length > 1 || (infoAlsoPlaying && onAirMedia.length >= 1);
  if (!conflictingPlaying) {
    return null;
  }
  const currentStep = cueState.currentStep;
  const currentContext: CueItemResolveContext = {
    scope: currentStep?.scope,
    slotId: slotIdFromCueSegmentAnchor(currentStep?.segmentAnchor) ?? cueState.activeSlotId,
    cueItemId: currentStep?.cueItemId,
  };
  let playingTarget: { itemId: string; scope?: string; slotId?: string; cueItemId?: string } | undefined;
  if (currentStep?.itemId && !isNextId(currentStep.itemId, currentContext)) {
    playingTarget = { itemId: currentStep.itemId, ...currentContext };
  } else {
    const entry = onAirMedia.find(
      (candidate) =>
        !isNextId(candidate.itemId, {
          scope: candidate.scope,
          slotId: candidate.slotId,
          cueItemId: candidate.cueItemId,
        }),
    );
    if (entry?.itemId) {
      playingTarget = {
        itemId: entry.itemId,
        scope: entry.scope,
        slotId: entry.slotId,
        cueItemId: entry.cueItemId,
      };
    }
  }
  if (!playingTarget) {
    return null;
  }
  if (isNextCuePreviewTarget(next, itemId, context)) {
    return 'next';
  }
  if (isNextCuePreviewTarget(playingTarget, itemId, context)) {
    return 'playing';
  }
  return 'other';
}

/** The row Next returns to while an aside or play-once insert is on air. */
export function isCueAsideResumeRow(
  cueState: FlowCueStateV1 | undefined,
  itemId: string,
  context?: CueItemResolveContext,
): boolean {
  if (cueState?.cueOverrideActive !== true || !cueState.resumeStep) {
    return false;
  }
  const step = cueState.resumeStep;
  if (step.itemId !== itemId && !compositeCueItemIdsEquivalent(step.itemId, itemId)) {
    return false;
  }
  if (context?.scope && step.scope && context.scope !== step.scope) {
    return false;
  }
  const stepSlotId = normalizeCueSlotId(slotIdFromCueSegmentAnchor(step.segmentAnchor));
  const contextSlotId = normalizeCueSlotId(context?.slotId);
  if (contextSlotId && stepSlotId && contextSlotId !== stepSlotId) {
    return false;
  }
  return true;
}

function isCurrentCueStepItem(cueState: FlowCueStateV1, itemId: string, context?: CueItemResolveContext): boolean {
  for (const candidateId of expandCompositeCueItemIds(itemId)) {
    if (matchesLiveCueCurrentStep(cueState, candidateId, context)) {
      return true;
    }
  }
  return false;
}

/** The info row of the current step's segment while the current media row shows it as fallback. */
function isCurrentSegmentScopeIdleInfo(
  cueState: FlowCueStateV1,
  cueItems: FlowCueStateV1['items'] | undefined,
  itemId: string,
  context: CueItemResolveContext | undefined,
): boolean {
  const step = cueState.currentStep;
  if (!step || !compositeSlugFromItemId(itemId) || context?.scope !== step.scope) {
    return false;
  }
  const stepSlotId = slotIdFromCueSegmentAnchor(step.segmentAnchor) ?? cueState.activeSlotId;
  if (normalizeCueSlotId(context?.slotId) !== normalizeCueSlotId(stepSlotId)) {
    return false;
  }
  return isScopeIdleCueItem(
    resolveCueItemState(cueItems, step.itemId, {
      cueItemId: step.cueItemId,
      scope: step.scope,
      slotId: stepSlotId,
    }),
  );
}

function resolveStoredCueItemPhase(
  cueItems: FlowCueStateV1['items'] | undefined,
  itemId: string,
  context: CueItemResolveContext | undefined,
  cueState: FlowCueStateV1,
): CueItemPhase | undefined {
  const entry = resolveCueItemState(cueItems, itemId, context);
  if (!entry?.phase) {
    return undefined;
  }
  if (entry.phase === 'on_air' && !cueSlotIdsMatch(entry.slotId, context?.slotId)) {
    return undefined;
  }
  if (
    (entry.phase === 'on_air' && entry.isCurrent === false) ||
    entry.phase === 'preloaded' ||
    entry.phase === 'loading_with_fallback'
  ) {
    if (isCurrentCueStepItem(cueState, itemId, context)) {
      return 'on_air';
    }
    if (entry.phase === 'on_air' && entry.isCurrent === false) {
      // Insert / play-once: the take stays on_air while currentStep can still be the auto row.
      // Once currentStep is that take, every other on_air row is stale — the return row is next.
      if (cueState.cueOverrideActive === true && isInsertOverrideTakeItem(itemId)) {
        const currentId = cueState.currentStep?.itemId;
        const currentEntry = currentId ? resolveCueItemState(cueItems, currentId, undefined) : undefined;
        const currentStepIsTheTake = currentEntry?.isCurrent === true && currentEntry.phase === 'on_air';
        if (currentStepIsTheTake && currentId !== itemId) {
          return 'idle';
        }
        return 'on_air';
      }
      if (cueState.cueOverrideActive === true) {
        return 'idle';
      }
      // Segment info shown after a media row ends: BE marks that segment's info row on_air.
      if (isCurrentSegmentScopeIdleInfo(cueState, cueItems, itemId, context)) {
        return 'on_air';
      }
      return 'idle';
    }
  }
  return entry.phase;
}

/** Resolve live cue item phase; honors currentStep even when activeSegmentKind lags. */
export function resolveLiveScopedCueItemPhase(
  cueState: FlowCueStateV1 | undefined,
  cueItems: FlowCueStateV1['items'] | undefined,
  itemId: string,
  context?: CueItemResolveContext,
): CueItemPhase | undefined {
  if (!cueState) {
    return undefined;
  }

  if (cueState.cueOverrideActive) {
    if (isCueAsideResumeRow(cueState, itemId, context)) {
      const entry = resolveCueItemState(cueItems, itemId, context);
      if (
        entry?.phase === 'skipped' ||
        entry?.phase === 'preloading' ||
        entry?.phase === 'loading_with_fallback' ||
        entry?.phase === 'played'
      ) {
        return entry.phase;
      }
      return 'preloaded';
    }
    // Info cards are not the aside. A composite insert that is the current take stays playing.
    if (!itemId.startsWith('media:') && !isCurrentCueStepItem(cueState, itemId, context)) {
      const entry = resolveCueItemState(cueItems, itemId, context);
      if (entry?.phase === 'on_air' || entry?.isCurrent) {
        return 'idle';
      }
      if (entry?.phase) {
        return entry.phase;
      }
      return 'idle';
    }
  }

  for (const candidateId of expandCompositeCueItemIds(itemId)) {
    if (matchesLiveCueCurrentStep(cueState, candidateId, context)) {
      const entry = resolveCueItemState(cueItems, candidateId, context);
      if (entry?.phase === 'skipped') {
        return 'skipped';
      }
      if (entry && isScopeIdleCueItem(entry)) {
        return entry.phase ?? 'idle';
      }
      if (entry?.phase === 'preloading') {
        return 'preloading';
      }
      return 'on_air';
    }
  }

  for (const candidateId of expandCompositeCueItemIds(itemId)) {
    const phase = resolveStoredCueItemPhase(cueItems, candidateId, context, cueState);
    if (phase) {
      return phase;
    }
  }

  return undefined;
}

/** Stable map key for cue rows that share the same timeline itemId (e.g. per-session session-info). */
/** Inverse of {@link buildCueControlItemKey}. scope/slot live in the key, not always on the entry value. */
export function parseCueControlItemKey(key: string): { itemId: string; scope?: string; slotId?: string } {
  const atIdx = key.indexOf('@');
  if (atIdx === -1) {
    return { itemId: key };
  }
  const itemId = key.slice(0, atIdx);
  const rest = key.slice(atIdx + 1);
  const colonIdx = rest.indexOf(':');
  if (colonIdx === -1) {
    return { itemId, scope: rest };
  }
  return { itemId, scope: rest.slice(0, colonIdx), slotId: rest.slice(colonIdx + 1) };
}

export function buildCueControlItemKey(itemId: string, scope?: string, slotId?: string): string {
  if (!scope) {
    return itemId;
  }
  if ((scope === 'session' || scope === 'slot') && slotId) {
    return `${itemId}@${scope}:${slotId}`;
  }
  return `${itemId}@${scope}`;
}

/** Resolve cue row state by timeline itemId (sheet-active payloads may key items by cueItemId). */
const CUE_ITEM_PHASE_RANK: Record<CueItemPhase, number> = {
  on_air: 60,
  played: 50,
  preloaded: 40,
  loading_with_fallback: 35,
  idle: 20,
  preloading: 10,
  skipped: 0,
};

function mergeCueItemPhase(getPhase?: CueItemPhase, wsPhase?: CueItemPhase): CueItemPhase | undefined {
  if (!getPhase) {
    return wsPhase;
  }
  if (!wsPhase) {
    return getPhase;
  }
  return CUE_ITEM_PHASE_RANK[getPhase] >= CUE_ITEM_PHASE_RANK[wsPhase] ? getPhase : wsPhase;
}

function findMatchingCueItemKeyAndEntry(
  items: FlowCueStateV1['items'],
  entry: CueItemStateEntry,
): { key: string; entry: CueItemStateEntry } | undefined {
  const scope = entry.scope;
  const slotId = entry.slotId;
  if (entry.cueItemId && items[entry.cueItemId]) {
    return { key: entry.cueItemId, entry: items[entry.cueItemId]! };
  }
  if (entry.itemId && scope) {
    const scopedKey = buildCueControlItemKey(entry.itemId, scope, slotId);
    const scoped = items[scopedKey];
    if (scoped) {
      return { key: scopedKey, entry: scoped };
    }
  }
  if (!entry.itemId) {
    return undefined;
  }
  for (const [key, candidate] of Object.entries(items)) {
    if (candidate.itemId !== entry.itemId) {
      continue;
    }
    if (scope && slotId) {
      if (candidate.scope === scope && candidate.slotId === slotId) {
        return { key, entry: candidate };
      }
      continue;
    }
    if (scope) {
      if (candidate.scope === scope) {
        return { key, entry: candidate };
      }
      continue;
    }
    return { key, entry: candidate };
  }
  return undefined;
}

/**
 * Choose which cue snapshot owns pointer / Next / override after a GET∪WS merge.
 *
 * Insert play-once uses a high override pointer whose Next is the resume PDF. After resume, GET
 * often has a *lower* auto pointer + the real following Next (e.g. transition composite). Pointer
 * magnitude alone would keep the insert overlay and leave Next stuck on the PDF for seconds.
 */
export function preferGetCueSnapshotOverWs(get: FlowCueStateV1, ws: FlowCueStateV1): boolean {
  const getOverride = get.cueOverrideActive === true;
  const wsOverride = ws.cueOverrideActive === true;
  if (getOverride !== wsOverride) {
    // Prefer the side that already left play-once insert.
    return !getOverride;
  }

  const getPointer = get.pointer ?? 0;
  const wsPointer = ws.pointer ?? 0;
  if (getPointer !== wsPointer) {
    return getPointer > wsPointer;
  }

  // Equal pointer: WS can still carry insert-era Next (resume PDF) while Current already matches.
  const wsCurrent = ws.currentStep?.itemId;
  const wsNext = ws.nextItemId;
  const getNext = get.nextItemId;
  if (wsCurrent && wsNext && wsCurrent === wsNext && getNext && getNext !== wsNext) {
    return true;
  }
  return false;
}

/**
 * GET baseline ∪ WS overlay.
 *
 * Pointer / next / currentStep follow the side that has advanced further — a lagging WS overlay
 * must not pin the cue list and Next preview after REST has already moved on. Per-item phase still
 * uses the more advanced phase when both sides mention the same row.
 *
 * Pointer magnitude is not true recency (backward jumps / insert→resume lower the pointer).
 * Post-cue-command paths must call `replaceCueStateFromGet` so hydrate/merge cannot revive a stale
 * higher-pointer overlay; insert→resume also prefers the non-override side (see
 * {@link preferGetCueSnapshotOverWs}).
 */
export function mergeCueStateFromGetAndWs(
  cueStateFromGet: FlowCueStateV1 | undefined,
  storeCueState: FlowCueStateV1 | undefined,
): FlowCueStateV1 | undefined {
  if (!storeCueState) {
    return cueStateFromGet;
  }
  if (!cueStateFromGet) {
    return storeCueState;
  }

  const preferGet = preferGetCueSnapshotOverWs(cueStateFromGet, storeCueState);
  const primary = preferGet ? cueStateFromGet : storeCueState;
  const secondary = preferGet ? storeCueState : cueStateFromGet;
  const primaryOverrideActive = primary.cueOverrideActive === true;

  // Start from the advanced side so stale `isCurrent` rows from the lagging snapshot cannot stick.
  const mergedItems: FlowCueStateV1['items'] = { ...primary.items };
  for (const [key, secondaryEntry] of Object.entries(secondary.items ?? {})) {
    const typed = secondaryEntry as CueItemStateEntry;
    const sameKeyEntry = mergedItems[key];
    const match = sameKeyEntry ? { key, entry: sameKeyEntry } : findMatchingCueItemKeyAndEntry(mergedItems, typed);
    if (!match) {
      // Lagging-only keys would reintroduce stale isCurrent / on_air flags — drop them.
      continue;
    }
    // Always write under the primary key. Secondary may key by scoped itemId while primary
    // keys by cueItemId — writing under `key` would duplicate the row and leave stuck isCurrent.
    const phase = mergeCueItemPhase(match.entry.phase, secondaryEntry.phase);
    mergedItems[match.key] = {
      ...secondaryEntry,
      ...match.entry,
      ...(phase ? { phase } : {}),
    };
  }

  return {
    ...secondary,
    ...primary,
    pointer: primary.pointer,
    totalSteps: primary.totalSteps ?? secondary.totalSteps,
    currentStep: primary.currentStep ?? secondary.currentStep,
    nextCueItemId: primary.nextCueItemId ?? secondary.nextCueItemId,
    nextItemId: primary.nextItemId ?? secondary.nextItemId,
    nextScope: primary.nextScope ?? secondary.nextScope,
    nextSlotId: primary.nextSlotId ?? secondary.nextSlotId,
    lookAheadShowMode: primary.lookAheadShowMode ?? secondary.lookAheadShowMode,
    // Do not revive insert override from the lagging side via `??`.
    cueOverrideActive: primaryOverrideActive ? true : undefined,
    resumePointer: primaryOverrideActive ? primary.resumePointer : undefined,
    resumeStep: primaryOverrideActive ? primary.resumeStep : undefined,
    activeSegmentKind: primary.activeSegmentKind ?? secondary.activeSegmentKind,
    activeSlotId: primary.activeSlotId ?? secondary.activeSlotId,
    isSegmentRunning: primary.isSegmentRunning ?? secondary.isSegmentRunning,
    mode: primary.mode ?? secondary.mode,
    mediaAgentAvailable: primary.mediaAgentAvailable ?? secondary.mediaAgentAvailable,
    items: mergedItems,
  };
}

export function resolveCueItemState(
  cueItems: FlowCueStateV1['items'] | undefined,
  itemId: string,
  context?: CueItemResolveContext,
): CueItemStateEntry | undefined {
  if (!cueItems) {
    return undefined;
  }

  if (context?.cueItemId) {
    const byCueItemId = cueItems[context.cueItemId];
    if (byCueItemId) {
      return byCueItemId;
    }
  }

  const needsSlotDisambiguation = context?.scope === 'session' || context?.scope === 'slot';

  if (context?.scope) {
    if (needsSlotDisambiguation && !context.slotId) {
      return undefined;
    }
    const scopedKey = buildCueControlItemKey(itemId, context.scope, context.slotId);
    const scoped = cueItems[scopedKey];
    if (scoped) {
      return scoped;
    }
    for (const candidateId of expandCompositeCueItemIds(itemId)) {
      if (candidateId === itemId) {
        continue;
      }
      const aliasScoped = cueItems[buildCueControlItemKey(candidateId, context.scope, context.slotId)];
      if (aliasScoped) {
        return aliasScoped;
      }
    }
    const matched = Object.values(cueItems).find(
      (entry) =>
        (entry.itemId === itemId || (entry.itemId != null && compositeCueItemIdsEquivalent(entry.itemId, itemId))) &&
        (entry as CueItemStateEntry).scope === context.scope &&
        cueSlotIdsMatch((entry as CueItemStateEntry).slotId, context.slotId),
    );
    if (matched) {
      return matched;
    }
    if (needsSlotDisambiguation) {
      return undefined;
    }
  }

  const direct = cueItems[itemId];
  if (direct) {
    return direct;
  }

  if (context?.scope) {
    return undefined;
  }

  return Object.values(cueItems).find(
    (entry) => entry.itemId != null && compositeCueItemIdsEquivalent(entry.itemId, itemId),
  );
}

const STAGE_PREFIXES = ['roomCam:'];
const MEDIA_PREFIXES = ['media:', 'composite:', 'screenshare:'];

/** Toggle stage or media selection independently. Stages and media can both be selected. */
export function toggleMainContentInSelection(selectedItemIds: string[], itemId: string): string[] {
  const isSelected = selectedItemIds.includes(itemId);
  if (isSelected) {
    return selectedItemIds.filter((id) => id !== itemId);
  }
  const isStage = STAGE_PREFIXES.some((p) => itemId.startsWith(p));
  const withoutSameGroup = selectedItemIds.filter(
    (id) => !(isStage ? STAGE_PREFIXES : MEDIA_PREFIXES).some((p) => id.startsWith(p)),
  );
  return [...withoutSameGroup, itemId];
}

export function buildPlayedItemIdsSet(liveContextState?: LiveContextStateDto): Set<string> {
  const entries = liveContextState?.playState?.playedItemIds;
  if (entries?.length) {
    return new Set(entries.map((e) => e.itemId));
  }
  return new Set((liveContextState?.playState?.layers ?? []).map((l) => l.itemId).filter((id): id is string => !!id));
}

function addPreviewItemId(ids: Set<string>, itemId: string | undefined): void {
  if (itemId) {
    ids.add(itemId);
  }
}

/** Current `_preview:*` / playState layers — insert chrome when cue currentStep still names the resume row. */
export function collectOnAirPreviewItemIds(liveContextState?: LiveContextStateDto): Set<string> {
  const ids = new Set<string>();
  const playState = liveContextState?.playState;
  for (const layer of playState?.layers ?? []) {
    addPreviewItemId(ids, layer.itemId);
  }
  for (const [key, entry] of Object.entries(playState?.byPlayer ?? {})) {
    if (key !== '_preview' && !key.startsWith('_preview:')) {
      continue;
    }
    for (const layer of entry?.layers ?? []) {
      addPreviewItemId(ids, layer.itemId);
    }
  }
  return ids;
}

/** Insert/play-once list chrome: the take target, not a fallback composite in Current layers. */
export function isInsertOverrideTakeItem(itemId: string): boolean {
  return itemId.startsWith('media:') || itemId.startsWith('virtualMedia:');
}

export function previewItemIdMatches(itemId: string, onAirIds: Set<string>): boolean {
  if (onAirIds.has(itemId)) {
    return true;
  }
  if (itemId.startsWith('media:')) {
    const bare = itemId.slice('media:'.length);
    return onAirIds.has(bare) || onAirIds.has(`media:${bare}`);
  }
  return onAirIds.has(`media:${itemId}`);
}

export const performItemRowBaseSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  p: 1,
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  alignSelf: 'stretch',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
};

export function isPerformItemOnAir(
  usesAutoCue: boolean,
  cueItemPhase: CueItemPhase | undefined,
  isStaged: boolean,
  isPlayed: boolean,
  isLiveCurrent = false,
): boolean {
  if (usesAutoCue) {
    // Play once / jump prep: cue phase can stay preloading or pre-insert while this row is already Current.
    return cueItemPhase === 'on_air' || isLiveCurrent;
  }
  return isStaged && isPlayed;
}

export function isCuePreloadIndicatorPhase(phase?: CueItemPhase): boolean {
  return phase === 'preloaded' || phase === 'preloading' || phase === 'loading_with_fallback';
}

/**
 * Discreet green dot: preloaded / preloading rows, on-air (loaded) items, and played items — which
 * remain preloaded (their layer is retained) and are therefore still loaded/replayable.
 */
export function shouldShowCueLoadedDot(phase?: CueItemPhase): boolean {
  return isCuePreloadIndicatorPhase(phase) || phase === 'on_air' || phase === 'played';
}

export type PerformItemRowStyleOptions = {
  /** When true, preloaded rows keep neutral styling; use {@link CuePreloadIndicator} instead. */
  discretePreload?: boolean;
  /** When true, on-air rows skip background tint (parent segment row carries it). */
  suppressOnAirBackground?: boolean;
  /** Scope-idle info composite showing while this cue row is stopped within the segment. */
  scopeIdle?: boolean;
  /** On-air / played accent (Control tab: info in simulation, error when live). Default error. */
  onAirAccent?: FlowControlSegmentAccent;
  /**
   * Row has been played but its media stays preloaded (replayable). Render it neutral (the play
   * icon conveys readiness) instead of the solid on-air/played accent, which reads as an error.
   * Truly on-air rows are unaffected (handled earlier).
   */
  playedStillLoaded?: boolean;
  /** Cue target shown in the Next preview — success border (on-air still wins). */
  isNextCue?: boolean;
};

export function isScopeIdleCueItem(entry?: {
  showingFallbackComposite?: boolean;
  phase?: CueItemPhase;
  isCurrent?: boolean;
}): boolean {
  return Boolean(entry?.showingFallbackComposite && entry?.isCurrent && entry.phase !== 'on_air');
}

export function getPerformItemRowStyles(
  isSelected: boolean,
  isPlayed: boolean,
  isOnAir = false,
  options?: PerformItemRowStyleOptions,
): SxProps<Theme> {
  const accent = options?.onAirAccent ?? 'error';
  if (options?.scopeIdle) {
    return {
      'border': '2px solid',
      'borderColor': 'info.main',
      '& .MuiIconButton-root': { color: 'info.main' },
    };
  }
  if (isOnAir) {
    return {
      ...(options?.suppressOnAirBackground
        ? {}
        : {
            'bgcolor': (theme) => alpha(theme.palette[accent].main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
            '& .MuiIconButton-root': { color: `${accent}.main` },
          }),
      border: '2px solid',
      borderColor: `${accent}.main`,
    };
  }
  if (options?.isNextCue) {
    return {
      'border': '2px solid',
      'borderColor': 'success.main',
      '& .MuiIconButton-root': { color: 'success.main' },
    };
  }
  if (isSelected && isPlayed) {
    return {
      'bgcolor': 'success.main',
      'color': 'success.contrastText',
      'border': '3px solid',
      'borderColor': `${accent}.main`,
      '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
      '& .MuiIconButton-root': { color: 'inherit' },
    };
  }
  if (isSelected && !isPlayed) {
    if (options?.discretePreload) {
      return {};
    }
    return {
      'bgcolor': 'success.main',
      'color': 'success.contrastText',
      'border': '1px solid',
      'borderColor': 'divider',
      '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
      '& .MuiIconButton-root': { color: 'inherit' },
    };
  }
  if (!isSelected && isPlayed) {
    // Played but still preloaded → neutral (green loaded dot conveys status); no solid red fill.
    if (options?.playedStillLoaded) {
      return {};
    }
    return {
      'bgcolor': `${accent}.main`,
      'color': `${accent}.contrastText`,
      'border': '1px solid',
      'borderColor': 'divider',
      '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
      '& .MuiIconButton-root': { color: 'inherit' },
    };
  }
  return {};
}

export function getPerformSelectableRowHoverSx(
  canSelect: boolean,
  isSelected: boolean,
  isPlayed: boolean,
  isOnAir = false,
  options?: PerformItemRowStyleOptions,
): SxProps<Theme> {
  const accent = options?.onAirAccent ?? 'error';
  if (!canSelect) return {};
  if (options?.scopeIdle) {
    return {
      '&:hover': {
        bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
      },
    };
  }
  if (isOnAir) {
    if (options?.suppressOnAirBackground) {
      return canSelect ? { '&:hover': { bgcolor: 'action.hover' } } : {};
    }
    return {
      '&:hover': {
        bgcolor: (theme) => alpha(theme.palette[accent].main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
      },
    };
  }
  if (options?.isNextCue) {
    return {
      '&:hover': {
        bgcolor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
      },
    };
  }
  if (options?.discretePreload && isSelected && !isPlayed) {
    return { '&:hover': { bgcolor: 'action.hover' } };
  }
  if (options?.playedStillLoaded && isPlayed && !isSelected) {
    return { '&:hover': { bgcolor: 'action.hover' } };
  }
  if (isSelected || isPlayed) {
    return {
      '&:hover': { bgcolor: isPlayed && !isSelected ? `${accent}.dark` : 'success.dark' },
    };
  }
  return { '&:hover': { bgcolor: 'action.hover' } };
}

/** Row is actively loading assets before playout (auto-preload or manual play). */
export function isCuePlayTransitionPhase(phase?: CueItemPhase): boolean {
  return phase === 'preloading' || phase === 'loading_with_fallback';
}

/**
 * Item is loaded and ready to (re)play — a play button is shown and preload is complete. Used to tint
 * the play button success/green (readiness), replacing the separate loaded dot. On-air (stop button)
 * and still-loading (spinner) rows are intentionally excluded.
 */
export function isCueItemPlayReady(phase?: CueItemPhase): boolean {
  return phase === 'preloaded' || phase === 'played';
}

/** Spinner on the play button until playout reaches on_air (or cue command is in flight). */
export function shouldShowCuePlayButtonSpinner(
  usesAutoCue: boolean,
  cueItemPhase: CueItemPhase | undefined,
  isItemCueCommandPending: boolean,
): boolean {
  if (!usesAutoCue) {
    return false;
  }
  if (isItemCueCommandPending) {
    return true;
  }
  return isCuePlayTransitionPhase(cueItemPhase);
}

export function resolveFlowControlPlayActionState(options: {
  usesAutoCue: boolean;
  cueItemPhase?: CueItemPhase;
  isItemCueCommandPending: boolean;
  canInteract: boolean;
  isSkipped?: boolean;
  isScopeIdle?: boolean;
  /** Red on-air chrome — insert can be live before cue phase catches up. */
  isOnAir?: boolean;
}): { showSpinner: boolean; showStop: boolean; disabled: boolean } {
  const showSpinner = shouldShowCuePlayButtonSpinner(
    options.usesAutoCue,
    options.cueItemPhase,
    options.isItemCueCommandPending,
  );
  const showStop =
    options.usesAutoCue &&
    (options.cueItemPhase === 'on_air' || options.isOnAir === true) &&
    !options.isScopeIdle &&
    !showSpinner;
  const disabled = !options.canInteract || showSpinner || Boolean(options.isSkipped);
  return { showSpinner, showStop, disabled };
}

export function toCompositeStagedItemId(slug: string): string {
  return `composite:${slug}`;
}

export function toMediaStagedItemId(mediaId: string): string {
  return `media:${mediaId}`;
}

export function toggleItemInSkippedList(skippedItemIds: string[], itemId: string): string[] {
  return skippedItemIds.includes(itemId) ? skippedItemIds.filter((id) => id !== itemId) : [...skippedItemIds, itemId];
}

export function cuePhaseToRowState(phase?: CueItemPhase): { staged: boolean; played: boolean } {
  switch (phase) {
    case 'on_air':
      return { staged: true, played: true };
    case 'preloaded':
    case 'preloading':
    case 'loading_with_fallback':
      return { staged: true, played: false };
    case 'played':
      return { staged: false, played: true };
    case 'skipped':
      return { staged: false, played: false };
    default:
      return { staged: false, played: false };
  }
}
